import axios from 'axios';
import type { Conversation } from '@gumtree/shared/src/types/message-centre';
import logger from '../logger';
import {
    GET_CONVERSATIONS_URL,
    POST_MESSAGE,
    GET_SINGLE_CONVERSATION,
    MESSAGES,
    MARK_AS_READ,
    BLOCK_USER,
    DELETE_CONVERSATION,
    UNBLOCK_USER,
    REPORT_USER,
    CAN_REPORT_USER,
    POST_REVIEW,
    CAN_REVIEW,
    GET_PAGINATED_CONVERSATIONS_URL,
    STREAM_TOKEN,
    BRAZE_NOTIFICATION,
    REVIEW_DELAY_RECORD,
    REVIEW_DELAY_JUDGE,
} from './url';
import {
    MarkMessagesAsReadPayload,
    BlockUserPayload,
    DeleteConversationPayload,
    SendMessagePayloadWithMetaData,
    ReportUserRequestPayload,
    CanReportConversationPayload,
    CanReviewPayload,
    CreateReviewPayload,
    PostBrazeNotificationPayload,
} from '../reducers/types';

export async function getConversations(bffUrl: string): Promise<Conversation[]> {
    try {
        const { data } = await axios.get(`${bffUrl}/${GET_CONVERSATIONS_URL}`);

        return data.conversations;
    } catch (e) {
        logger.error('Error calling getConversations', e);
        throw e;
    }
}

export async function getStreamToken(bffUrl: string): Promise<string> {
    try {
        const { data } = await axios.get(`${bffUrl}/${STREAM_TOKEN}`);

        return data.token;
    } catch (e) {
        logger.error('Error calling getStreamToken', e);
        throw e;
    }
}

export async function getPaginatedConversations(
    bffUrl: string,
    pageNumber = 0
): Promise<{ conversations: Conversation[]; hasMore: boolean }> {
    try {
        const {
            data: { conversations, hasMore },
        } = await axios.get(`${bffUrl}/${GET_PAGINATED_CONVERSATIONS_URL}?offset=${pageNumber}`);

        return { conversations, hasMore };
    } catch (e) {
        logger.error('Error calling getPaginatedConversations', e);
        throw e;
    }
}

export async function postMessage(
    messageData: SendMessagePayloadWithMetaData,
    bffUrl: string
): Promise<string> {
    try {
        const { data: messagedId } = await axios.post(`${bffUrl}/${POST_MESSAGE}`, messageData);

        return messagedId;
    } catch (e) {
        logger.error('Error calling postMessage', e);
        throw e;
    }
}

export async function getConversation(
    { adId, sellerId, buyerId },
    bffUrl: string
): Promise<Conversation> {
    try {
        const { data } = await axios.get(`${bffUrl}/${GET_SINGLE_CONVERSATION}`, {
            params: { adId, sellerId, buyerId },
        });

        return data;
    } catch (e) {
        logger.error('Error calling getConversation', e);
        throw e;
    }
}

export async function putMessagesAsRead(
    { adId, sellerId, buyerId, latestMessageId }: MarkMessagesAsReadPayload,
    bffUrl: string
): Promise<boolean> {
    try {
        const payload = { adId, sellerId, buyerId };
        const { data: isUpdated } = await axios.put(
            `${bffUrl}/${MESSAGES}/${latestMessageId}/${MARK_AS_READ}`,
            payload
        );

        return isUpdated;
    } catch (e) {
        logger.error('Error calling putMessageAsRead', e);
        throw e;
    }
}

export async function blockUser(payload: BlockUserPayload, bffUrl: string): Promise<boolean> {
    try {
        const {
            data: { isBlocked },
        } = await axios.post(`${bffUrl}/${BLOCK_USER}`, payload);

        return isBlocked;
    } catch (e) {
        logger.error('Error calling blockUser', e);
        throw e;
    }
}

export async function unblockUser(payload: BlockUserPayload, bffUrl: string): Promise<boolean> {
    try {
        const {
            data: { isUnblocked },
        } = await axios.post(`${bffUrl}/${UNBLOCK_USER}`, payload);

        return isUnblocked;
    } catch (e) {
        logger.error('Error calling unblockUser', e);
        throw e;
    }
}

export async function deleteConversation(
    payload: DeleteConversationPayload,
    bffUrl: string
): Promise<boolean> {
    try {
        const { data: isDeleted } = await axios.post(`${bffUrl}/${DELETE_CONVERSATION}`, payload);

        return isDeleted;
    } catch (e) {
        logger.error('Error calling deleteConversation', e);
        throw e;
    }
}

export async function reportUser(
    payload: ReportUserRequestPayload,
    bffUrl: string
): Promise<number> {
    try {
        const { data: id } = await axios.post(`${bffUrl}/${REPORT_USER}`, payload);

        return id;
    } catch (e) {
        logger.error('Error calling deleteConversation', e);
        throw e;
    }
}

export async function getCanReportConversation(
    payload: CanReportConversationPayload,
    bffUrl: string
): Promise<boolean> {
    try {
        const { data: canReport } = await axios.get(`${bffUrl}/${CAN_REPORT_USER}`, {
            params: {
                conversationId: payload.conversationId,
                userId: payload.userId,
            },
        });

        return canReport;
    } catch (e) {
        logger.error('Error calling deleteConversation', e);
        throw e;
    }
}

export async function getCanUserReview(
    {
        adId,
        reviewerUserId,
        revieweeUserId,
        conversationId,
        l1CategoryId,
        l2CategoryId,
        sellerType,
    }: CanReviewPayload,
    bffUrl: string
): Promise<boolean> {
    const config = {
        headers: {
            'Content-Type': 'application/json',
        },
        params: {
            adId,
            reviewerUserId,
            revieweeUserId,
            conversationId,
            l1CategoryId,
            l2CategoryId,
            sellerType,
        },
    };

    try {
        const result = await axios.post(`${bffUrl}/${CAN_REVIEW}`, {}, config);
        return result.data;
    } catch (e) {
        logger.error('Error calling getCanUserReview', e);
        throw e;
    }
}

export async function createReview(
    {
        adId,
        sellerId,
        buyerId,
        id,
        categoryId,
        title,
        granularResponse,
        rating,
        combinedFeedback,
        positiveFeedback,
        negativeFeedback,
        isQA,
        source,
    }: CreateReviewPayload,
    bffUrl: string
) {
    const config = {
        headers: {
            'Content-Type': 'application/json',
        },
        params: {
            adId,
            sellerId,
            buyerId,
            id,
            categoryId,
            title,
            feedback: granularResponse,
            rating,
            combinedFeedback,
            positiveFeedback,
            negativeFeedback,
            isQA,
            source,
        },
    };

    try {
        const { data } = await axios.post(`${bffUrl}/${POST_REVIEW}`, {}, config);
        return data;
    } catch (e) {
        logger.error('Error calling createReview', e);
        throw e;
    }
}

/**
 * send braze notification
 * @param param0
 * @param bffUrl
 * @returns
 */
export async function postBrazeNotification(
    {
        adId,
        conversationId,
        reviewerUserId,
        reviewerUserName,
        revieweeUserId,
        revieweeUserName,
        direction,
        canReverseReview,
        status,
        rating,
    }: PostBrazeNotificationPayload,
    bffUrl: string
) {
    const params = {
        adId,
        conversationId,
        reviewerUserId,
        reviewerUserName,
        revieweeUserId,
        revieweeUserName,
        direction,
        canReverseReview,
        status,
        rating,
    };
    const config = {
        headers: {
            'Content-Type': 'application/json',
        },
    };
    try {
        const { data } = await axios.post(`${bffUrl}/${BRAZE_NOTIFICATION}`, params, config);
        return data;
    } catch (e) {
        logger.error('Error calling postBrazeNotification', e);
        throw e;
    }
}

export async function judgeReviewDelay(
    { conversationId, reviewerUserId, revieweeUserId },
    bffUrl: string
): Promise<any> {
    try {
        const payload = { conversationId, reviewerUserId, revieweeUserId };
        const { data } = await axios.post(`${bffUrl}/${REVIEW_DELAY_JUDGE}`, payload);
        return data;
    } catch (e) {
        logger.error('Error calling judgeRatingDelay', e);
        throw e;
    }
}

export async function recordReviewDelay(
    { conversationId, reviewerUserId, revieweeUserId },
    bffUrl: string
): Promise<any> {
    try {
        const payload = { conversationId, reviewerUserId, revieweeUserId };
        const { data } = await axios.post(`${bffUrl}/${REVIEW_DELAY_RECORD}`, payload);
        return data;
    } catch (e) {
        logger.error('Error calling recordRatingDelay', e);
        throw e;
    }
}
